import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import config from '../../../../app/config';
import MessageBox from '../../../../util/messageBox';
import uuidv4 from 'uuid/v4';
import _ from 'lodash';
import {MentalHealthClassification} from '../../../../controls/agyle/dictionaries';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {mentalHealthClassification} from '../../../../store/reducers/dicts';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import {calculateArrivalDate} from '../../../../util/patientUtil';

const COLUMNS_V3 = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('status', 'Status Classification Code', 2, 'MentalHealthActLegalStatusClassificationCode', 'dict', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('startDate', 'Start Date', 3, 'sDate', 'string', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('startTime', 'Start Time', 4, 'sTime', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('expiryDate', 'Expiry Date', 5, 'eDate', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('expiryTime', 'Expiry Time', 6, 'eTime', 'string', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('action', 'Action', 7, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
];

const COLUMNS_V4 = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('status', 'Status Classification Code', 2, 'MentalHealthActLegalStatusClassificationCode', 'dict', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('startTimestamp', 'Start Timestamp', 3, 'sTimestamp', 'date', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('expiryTimestamp', 'Expiry Timestamp', 4, 'eTimestamp', 'date', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('action', 'Action', 5, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
];

const MentalHealth = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const {version, isSmall} = props;

    // Multi Entry list
    const [list, setList] = useState([]);

    // Accordion
    const [expanded, setExpanded] = React.useState('');

    // Data
    const [dataForm, setDataForm] = useState({
        mentalHealthCode: null,
        startDate: null,
        startTime: null,
        expiryDate: null,
        expiryTime: null,
        startTimestamp: null,
        expiryTimestamp: null,
    });
    const [pickers, setPickers] = useState({
        startDate: null,
        startTime: null,
        expiryDate: null,
        expiryTime: null,
        startTimestamp: null,
        expiryTimestamp: null,
    });

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, false);

    // Validation
    const [error, setError] = useState(false);
    const errorPicker = useRef({
        startDate: null,
        startTime: null,
        expiryDate: null,
        expiryTime: null,
        startTimestamp: null,
        expiryTimestamp: null,
    });

    // Dictionaries
    const mentalHealthDict = useSelector(mentalHealthClassification);

    // Init
    useEffect(() => {
        if (props.patient?.studies?.EmergencyCare?.PersonGroupPatient.MentalHealthActLegalStatus) {
            let clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.PersonGroupPatient.MentalHealthActLegalStatus);
            let pushList = [];
            _.forEach(clone, element => {
                let data = {
                    ...element,
                    MentalHealthActLegalStatusClassificationCode: _.find(mentalHealthDict, {__code: element.MentalHealthActLegalStatusClassificationCode}),
                };
                if (version === 'V3') {
                    data = {
                        ...data,
                        sDate: getLocalDateText(element, 'date', 'start'),
                        sTime: getLocalDateText(element, 'time', 'start'),
                        eDate: getLocalDateText(element, 'date', 'expiry'),
                        eTime: getLocalDateText(element, 'time', 'expiry'),
                    };
                } else if (version === 'V4') {
                    data = {
                        ...data,
                        sTimestamp: moment.utc(element.MentalHealthActLegalStatusClassificationAssignmentPeriodStartTimestamp, config.ecdsDateTimeFormat),
                        eTimestamp: moment.utc(element.MentalHealthActLegalStatusClassificationExpiryTimestamp, config.ecdsDateTimeFormat),
                    };
                }
                pushList.push(data);
            });
            setList([...pushList]);
        }
    }, []);


    const setDataDictionary = () => {
        let formattedList = _.cloneDeep(list);

        if (props.ecdsData) {
            for (const element of formattedList) {
                element.MentalHealthActLegalStatusClassificationCode = element.MentalHealthActLegalStatusClassificationCode.__code;
                delete element.sDate;
                delete element.sTime;
                delete element.eDate;
                delete element.eTime;
                delete element.sTimestamp;
                delete element.eTimestamp;
            }
        }
        props.setData(formattedList);
    };

    useEffect(() => {
        setDataDictionary();
    }, [list]);

    const getLocalDateText = (obj, type, from) => {
        const local = from === 'start' ? UtilsMomentJS.getLocalDateFromUTCECDSFormat(obj.StartDate_MentalHealthActLegalStatusClassificationAssignmentPeriod, obj.StartTime_MentalHealthActLegalStatusClassificationAssignmentPeriod)
            : UtilsMomentJS.getLocalDateFromUTCECDSFormat(obj.ExpiryDate_MentalHealthActLegalStatusClassification, obj.ExpiryTime_MentalHealthActLegalStatusClassification);

        if (local) {
            return type === 'date' ? local.format(config.defaultDateFormat) : local.format(config.defaultTimeFormat);
        }
        return '-';
    };

    const addToList = () => {
        if (validation()) {
            const startUTC = UtilsMomentJS.getUTCDateFromECDSFormat(pickers.startDate, pickers.startTime);
            const expiryUTC = UtilsMomentJS.getUTCDateFromECDSFormat(pickers.expiryDate, pickers.expiryTime);

            let data = {
                id: uuidv4() + '',
                MentalHealthActLegalStatusClassificationCode: dataForm.mentalHealthCode,
                StartDate_MentalHealthActLegalStatusClassificationAssignmentPeriod: (pickers.startDate && startUTC) ? startUTC.format(config.ecdsDateFormat) : '',
                StartTime_MentalHealthActLegalStatusClassificationAssignmentPeriod: (pickers.startTime && startUTC) ? startUTC.format(config.ecdsTimeFormat) : '',
                ExpiryDate_MentalHealthActLegalStatusClassification: (pickers.expiryDate && expiryUTC) ? expiryUTC.format(config.ecdsDateFormat) : '',
                ExpiryTime_MentalHealthActLegalStatusClassification: (pickers.expiryTime && expiryUTC) ? expiryUTC.format(config.ecdsTimeFormat) : '',
                MentalHealthActLegalStatusClassificationAssignmentPeriodStartTimestamp: pickers.startTimestamp ? moment(pickers.startTimestamp).utc().format(config.ecdsDateTimeFormat) : '',
                MentalHealthActLegalStatusClassificationExpiryTimestamp: pickers.expiryTimestamp ? moment(pickers.expiryTimestamp).utc().format(config.ecdsDateTimeFormat) : '',
            };
            data = {
                ...data,
                sDate: getLocalDateText(data, 'date', 'start'),
                sTime: getLocalDateText(data, 'time', 'start'),
                eDate: getLocalDateText(data, 'date', 'expiry'),
                eTime: getLocalDateText(data, 'time', 'expiry'),
                sTimestamp: pickers.startTimestamp,
                eTimestamp: pickers.expiryTimestamp,
            };
            setList((oldArray) => [...oldArray, data]);
            clearData();
        }
    };

    const validation = () => {
        setError(!dataForm.mentalHealthCode);
        return !error && !errorPicker.current.startDate && !errorPicker.current.startTime && !errorPicker.current.expiryDate && !errorPicker.current.expiryTime && !!dataForm.mentalHealthCode;
    };

    const clearData = () => {
        const clearDataForm = {
            mentalHealthCode: null,
            startDate: null,
            startTime: null,
            expiryDate: null,
            expiryTime: null,
            sTimestamp: null,
            eTimestamp: null,
        };
        setPickers({
            startDate: null,
            startTime: null,
            expiryDate: null,
            expiryTime: null,
            sTimestamp: null,
            eTimestamp: null,
        });

        setDataForm(prevState => ({...prevState, ...clearDataForm}));
        setError(false);
    };

    const dateHandler = (name, event) => {
        if (event) {
            const date = moment(event).set('second', 0);
            // eslint-disable-next-line default-case
            switch (name) {
                case 'StartDate_MentalHealthActLegalStatusClassificationAssignmentPeriod': {
                    setPickers(prevState => ({...prevState, 'startDate': date}));
                    break;
                }
                case 'StartTime_MentalHealthActLegalStatusClassificationAssignmentPeriod': {
                    setPickers(prevState => ({...prevState, 'startTime': date}));
                    break;
                }
                case 'ExpiryDate_MentalHealthActLegalStatusClassification': {
                    setPickers(prevState => ({...prevState, 'expiryDate': date}));
                    break;
                }
                case 'ExpiryTime_MentalHealthActLegalStatusClassification': {
                    setPickers(prevState => ({...prevState, 'expiryTime': date}));
                    break;
                }
                case 'MentalHealthActLegalStatusClassificationAssignmentPeriodStartTimestamp': {
                    setPickers(prevState => ({...prevState, 'startTimestamp': date}));
                    break;
                }
                case 'MentalHealthActLegalStatusClassificationExpiryTimestamp': {
                    setPickers(prevState => ({...prevState, 'expiryTimestamp': date}));
                    break;
                }
            }
        }
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => setExpanded(newExpanded ? panel : false);

    const deleteReferral = () => {
        setList(prevState => prevState.filter(value => value.id !== deleteItem.id));
        setDeleteItem(null);
        closeWarning();
        clearData();
    };

    const handleDeleteItem = (item) => {
        setDeleteItem(item);
        openWarning();
    };

    const renderTable = () => {
        if (list) {
            return (
                <AgyleAccordion
                    expanded={expanded === 'mental'}
                    onChange={handleAccordionChange('mental')}
                    label={t('Table')}
                >
                    <AgyleTable
                        columns={version === 'V3' ? COLUMNS_V3 : COLUMNS_V4}
                        data={list}
                        deleteItem={handleDeleteItem}
                    />
                </AgyleAccordion>
            );
        }
    };

    if (props.accordion !== 'Mental Health Act Legal Status') return null;

    return (
        <div className={classes.root}>
            {!_.isEmpty(list) && renderTable()}

            <Grid container spacing={2} style={{marginTop: 1}}>
                <Grid item xs={12}>
                    <MentalHealthClassification
                        label={t('Mental Health Classification')}
                        value={dataForm.mentalHealthCode}
                        onChange={(_e, val) => {
                            setDataForm(prevState => ({
                                ...prevState,
                                mentalHealthCode: val,
                            }));
                            setError(!props.checkLenght(2, 2, val?.__code));
                        }}
                        error={error}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                {version === 'V3' &&
                    <>
                        <Grid item xs={isSmall ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('Start Date')}
                                value={pickers.startDate}
                                onChange={(e) => dateHandler('StartDate_MentalHealthActLegalStatusClassificationAssignmentPeriod', e)}
                                type="date"
                                minDate={moment(calculateArrivalDate(props.patient))}
                                maxDate={moment()}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    startDate: event,
                                }}
                                error={error.startDate}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('Start Time')}
                                value={pickers.startTime}
                                onChange={(e) => dateHandler('StartTime_MentalHealthActLegalStatusClassificationAssignmentPeriod', e)}
                                type="time"
                                minDate={
                                    pickers.startDate ? moment(pickers.startDate).format('L') === moment(moment(calculateArrivalDate(props.patient))).format('L')
                                        ? moment(calculateArrivalDate(props.patient)) : null : null
                                }
                                maxDate={pickers.startDate ? moment(pickers.startDate).format('L') === moment().format('L') ? moment() : null : null}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    startTime: event,
                                }}
                                error={error.startTime}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('Expiry Date')}
                                value={pickers.expiryDate}
                                onChange={(e) => dateHandler('ExpiryDate_MentalHealthActLegalStatusClassification', e)}
                                type="date"
                                minDate={moment(calculateArrivalDate(props.patient))}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    expiryDate: event,
                                }}
                                error={error.expiryDate}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 3}>
                            <AgyleDatePicker
                                label={t('Expiry Time')}
                                value={pickers.expiryTime}
                                onChange={(e) => dateHandler('ExpiryTime_MentalHealthActLegalStatusClassification', e)}
                                type="time"
                                minDate={
                                    pickers.expiryDate ? moment(pickers.expiryDate).format('L') === moment(moment(calculateArrivalDate(props.patient))).format('L')
                                        ? moment(calculateArrivalDate(props.patient)) : null : null
                                }
                                maxDate={pickers.expiryDate ? moment(pickers.expiryDate).format('L') === moment().format('L') ? moment() : null : null}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    expiryTime: event,
                                }}
                                error={error.expiryTime}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    </>
                }

                {version === 'V4' &&
                    <>
                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleDatePicker
                                label={t('Classification Assignment Period Start Timestamp')}
                                value={pickers.startTimestamp}
                                onChange={(e) => dateHandler('MentalHealthActLegalStatusClassificationAssignmentPeriodStartTimestamp', e)}
                                type="dateTime"
                                minDate={calculateArrivalDate(props.patient)}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    startTimestamp: event,
                                }}
                                error={error.startTimestamp}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>

                        <Grid item xs={isSmall ? 12 : 6}>
                            <AgyleDatePicker
                                label={t('Classification Expiry Timestamp')}
                                value={pickers.expiryTimestamp}
                                onChange={(e) => dateHandler('MentalHealthActLegalStatusClassificationExpiryTimestamp', e)}
                                type="dateTime"
                                minDate={calculateArrivalDate(props.patient)}
                                onError={event => errorPicker.current = {
                                    ...errorPicker.current,
                                    expiryTimestamp: event,
                                }}
                                error={error.expiryTimestamp}
                                noBorder={false}
                                withNewBackground
                                size="large"
                            />
                        </Grid>
                    </>
                }

                <Grid item xs={12}>
                    <AgyleButton
                        variant="contained"
                        onClick={addToList}
                        style={{marginLeft: 'auto'}}
                    >
                        {t('Add Record')}
                    </AgyleButton>
                </Grid>
            </Grid>

            <MessageBox
                open={isWarningOpen}
                onClose={closeWarning}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={deleteReferral}
                warning
            />
        </div>
    );
};

export default MentalHealth;
